import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { api, type CheckinList, type CheckinRow, type Me } from '../api.js';
import { filterRows } from '../lib/match.js';
import { BRAND } from '../branding.js';

const REFRESH_MS = 7000;

type StatusFilter = 'all' | 'not' | 'in';

// Чек-ин держит собственную сессию через /api/checkin/* (вне общего AuthProvider,
// который ходит в /admin/api/me — а волонтёру /admin/api/* закрыт хуком в app.ts;
// раньше /admin ещё закрывал Cloudflare Access, его сняли 13.10.2026).
export function CheckIn(): JSX.Element {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .checkinMe()
      .then((me) => {
        if (alive) setUser(me);
      })
      .catch(() => {
        if (alive) setUser(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const me = await api.checkinLogin(email, password);
    setUser(me);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.checkinLogout();
    } finally {
      setUser(null);
    }
  }, []);

  if (loading) {
    return <Centered>Загрузка…</Centered>;
  }
  if (!user) {
    return <CheckInLogin onLogin={login} />;
  }
  return <CheckInBoard onLogout={logout} isAdmin={user.role === 'superadmin' || user.role === 'admin'} />;
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'system-ui, sans-serif',
        color: '#15161A',
        background: '#F5F2EC',
      }}
    >
      {children}
    </div>
  );
}

function CheckInLogin({
  onLogin,
}: {
  onLogin: (email: string, password: string) => Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await onLogin(email.trim(), password);
    } catch {
      setErr('Неверный логин или пароль');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centered>
      <form
        onSubmit={submit}
        style={{
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 28 }}>
          Чек-ин · AI в деле TLV
        </div>
        <div style={{ color: '#5b5d63', fontSize: 14, marginBottom: 4 }}>
          Вход для волонтёров
          {!BRAND.passwordLogin && (
            <>
              . Админы входят через Google на{' '}
              <a href="/login" style={{ color: 'inherit' }}>
                странице входа
              </a>
              {' '}со своего телефона и открывают /checkin: на общем планшете
              осталась бы сессия админки.
            </>
          )}
        </div>
        <input
          type="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
        <input
          type="password"
          placeholder="пароль"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
        {err && <div style={{ color: '#c0392b', fontSize: 14 }}>{err}</div>}
        <button type="submit" disabled={busy} style={primaryBtnStyle}>
          {busy ? 'Вход…' : 'Войти'}
        </button>
      </form>
    </Centered>
  );
}

function CheckInBoard({
  onLogout,
  isAdmin,
}: {
  onLogout: () => Promise<void>;
  isAdmin: boolean;
}): JSX.Element {
  const [data, setData] = useState<CheckinList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [vipBusy, setVipBusy] = useState(false);
  const [vipMsg, setVipMsg] = useState<string | null>(null);
  const mounted = useRef(true);
  const searchRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await api.checkinList();
      if (mounted.current) {
        setData(res);
        setErr(null);
      }
    } catch {
      if (mounted.current) setErr('Не удалось загрузить список');
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [load]);

  const mark = async (id: string): Promise<void> => {
    setPendingId(id);
    try {
      await api.checkinMark(id);
      await load();
    } catch {
      setErr('Ошибка отметки');
    } finally {
      if (mounted.current) setPendingId(null);
    }
  };

  const undo = async (id: string): Promise<void> => {
    setPendingId(id);
    try {
      await api.checkinUndo(id);
      await load();
    } catch {
      setErr('Ошибка отмены');
    } finally {
      if (mounted.current) setPendingId(null);
    }
  };

  const syncVip = async (): Promise<void> => {
    setVipBusy(true);
    setVipMsg(null);
    try {
      const res = await api.checkinSyncVip();
      await load();
      if (mounted.current) setVipMsg(`VIP: ${res.vip}`);
    } catch {
      if (mounted.current) setVipMsg('Ошибка синка VIP');
    } finally {
      if (mounted.current) setVipBusy(false);
    }
  };

  const rows = data?.rows ?? [];
  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  // Полная сортировка: совпавшие по поиску/фильтру id, непришедшие сверху, дальше по имени.
  const computeOrder = useCallback(
    (rs: CheckinRow[], query: string, filt: StatusFilter): string[] => {
      let list = filterRows(rs, query);
      if (filt === 'not') list = list.filter((r) => !r.checkedInAt);
      else if (filt === 'in') list = list.filter((r) => r.checkedInAt);
      return [...list]
        .sort((a, b) => {
          const ai = a.checkedInAt ? 1 : 0;
          const bi = b.checkedInAt ? 1 : 0;
          if (ai !== bi) return ai - bi;
          const av = a.vip ? 0 : 1;
          const bv = b.vip ? 0 : 1;
          if (av !== bv) return av - bv; // VIP выше внутри своей группы
          return a.name.localeCompare(b.name, 'ru');
        })
        .map((r) => r.id);
    },
    [],
  );

  // Порядок карточек «замораживается» и пересортируется только при смене запроса
  // или фильтра («следующий поиск»). Отметка прихода и автообновление НЕ двигают
  // карточку — она меняет вид (зелёная), но остаётся на месте, чтобы волонтёр не
  // тыкнул мимо. Новые продажи дописываются в конец, удалённые — убираются.
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const orderKey = `${q} ${statusFilter}`;
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (lastKey.current !== orderKey) {
      lastKey.current = orderKey;
      setOrderIds(computeOrder(rows, q, statusFilter));
      return;
    }
    setOrderIds((prev) => {
      const prevSet = new Set(prev);
      const kept = prev.filter((id) => rowById.has(id));
      const fresh = computeOrder(rows, q, statusFilter).filter((id) => !prevSet.has(id));
      if (fresh.length === 0 && kept.length === prev.length) return prev;
      return [...kept, ...fresh];
    });
  }, [orderKey, rows, rowById, computeOrder, q, statusFilter]);

  const visible = useMemo(
    () => orderIds.map((id) => rowById.get(id)).filter((r): r is CheckinRow => Boolean(r)),
    [orderIds, rowById],
  );

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#F5F2EC',
        color: '#15161A',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#15161A',
          color: '#fff',
          padding: '12px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>Чек-ин · AI в деле TLV</div>
          <button onClick={() => void onLogout()} style={ghostBtnStyle}>
            Выйти
          </button>
        </div>
        <div style={{ marginTop: 8, fontSize: 15 }}>
          Отмечено{' '}
          <b style={{ color: '#8FA8FF' }}>{data ? data.checkedIn : '—'}</b> /{' '}
          {data ? data.total : '—'}
        </div>
        <div style={{ position: 'relative', marginTop: 10 }}>
          <input
            ref={searchRef}
            autoFocus
            placeholder="Поиск: имя, email или телефон"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            inputMode="search"
            autoCapitalize="none"
            autoCorrect="off"
            style={{
              ...inputStyle,
              width: '100%',
              boxSizing: 'border-box',
              paddingRight: 44,
              // Имя на иврите — справа налево с правильным порядком слов.
              unicodeBidi: 'plaintext',
            }}
          />
          {q && (
            <button
              type="button"
              aria-label="Очистить поиск"
              onClick={() => {
                setQ('');
                searchRef.current?.focus();
              }}
              style={clearBtnStyle}
            >
              ×
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <FilterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
            Все
          </FilterChip>
          <FilterChip active={statusFilter === 'not'} onClick={() => setStatusFilter('not')}>
            Не пришли
          </FilterChip>
          <FilterChip active={statusFilter === 'in'} onClick={() => setStatusFilter('in')}>
            Пришли
          </FilterChip>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => void syncVip()}
              disabled={vipBusy}
              style={vipSyncBtnStyle}
            >
              {vipBusy ? 'Обновляю VIP…' : '↻ Обновить VIP из Notion'}
            </button>
            {vipMsg && <span style={{ fontSize: 13, color: '#8FA8FF' }}>{vipMsg}</span>}
          </div>
        )}
      </div>

      {err && (
        <div style={{ padding: '8px 16px', color: '#c0392b', fontSize: 14 }}>{err}</div>
      )}

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!data && <div style={{ color: '#5b5d63', padding: 16 }}>Загрузка…</div>}
        {data && visible.length === 0 && (
          <div style={{ color: '#5b5d63', padding: 16, textAlign: 'center' }}>
            {q ? 'Ничего не найдено' : 'Список пуст'}
          </div>
        )}
        {visible.map((r) => (
          <Card key={r.id} row={r} busy={pendingId === r.id} onMark={mark} onUndo={undo} />
        ))}
      </div>
      <div style={{ height: 40 }} />
    </div>
  );
}

function Card({
  row,
  busy,
  onMark,
  onUndo,
}: {
  row: CheckinRow;
  busy: boolean;
  onMark: (id: string) => void;
  onUndo: (id: string) => void;
}): JSX.Element {
  const arrived = Boolean(row.checkedInAt);
  return (
    <div
      style={{
        background: arrived ? '#EAF3EC' : '#fff',
        border: `1px solid ${arrived ? '#bcd9c4' : 'rgba(21,22,26,.14)'}`,
        borderRadius: 12,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: row.vip ? 'inset 4px 0 0 #C9A227' : undefined, // золотой акцент у VIP
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 19, lineHeight: 1.2 }}>
            <bdi>{row.name}</bdi>
          </div>
          <div style={{ fontSize: 13, color: '#5b5d63', marginTop: 3, wordBreak: 'break-word' }}>
            {row.email}
            {row.phone ? ` · ${row.phone}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {row.ticketNumber && (
            <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
              {row.ticketNumber}
            </div>
          )}
          {row.vip && <span style={vipBadgeStyle}>VIP</span>}
          <span
            style={{
              display: 'inline-block',
              marginTop: 4,
              fontSize: 10,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: row.ticketType === 'promo' ? '#9a6b00' : '#1F4DFF',
              background: row.ticketType === 'promo' ? '#fbeccb' : '#e6ecff',
              borderRadius: 4,
              padding: '2px 6px',
            }}
          >
            {row.ticketType === 'promo' ? 'промо' : 'билет'}
          </span>
        </div>
      </div>

      {arrived ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ color: '#2e7d32', fontWeight: 600, fontSize: 15 }}>
            ✓ Пришёл {fmtTime(row.checkedInAt)}
          </div>
          <button onClick={() => onUndo(row.id)} disabled={busy} style={undoBtnStyle}>
            {busy ? '…' : 'Отменить'}
          </button>
        </div>
      ) : (
        <button onClick={() => onMark(row.id)} disabled={busy} style={markBtnStyle}>
          {busy ? '…' : 'Check in'}
        </button>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '8px 10px',
        borderRadius: 999,
        border: '1px solid ' + (active ? '#8FA8FF' : 'rgba(255,255,255,.25)'),
        background: active ? '#1F4DFF' : 'transparent',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 16, // ≥16 чтобы iOS не зумил
  borderRadius: 8,
  border: '1px solid rgba(21,22,26,.2)',
  outline: 'none',
  color: '#15161A', // тёмный текст: иначе в тёмной шапке инпут наследует белый и текст не виден
  background: '#fff',
};

const clearBtnStyle: React.CSSProperties = {
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 32,
  height: 32,
  borderRadius: 16,
  border: 'none',
  background: 'rgba(21,22,26,.10)',
  color: '#15161A',
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 16,
  fontWeight: 700,
  color: '#fff',
  background: '#1F4DFF',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};

const markBtnStyle: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 16,
  fontWeight: 700,
  color: '#fff',
  background: '#1F4DFF',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  minHeight: 48,
};

const undoBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 14,
  fontWeight: 600,
  color: '#15161A',
  background: 'transparent',
  border: '1px solid rgba(21,22,26,.3)',
  borderRadius: 8,
  cursor: 'pointer',
  minHeight: 40,
};

const ghostBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  color: '#fff',
  background: 'transparent',
  border: '1px solid rgba(255,255,255,.3)',
  borderRadius: 8,
  cursor: 'pointer',
};

const vipBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  marginTop: 4,
  marginRight: 4,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '.08em',
  color: '#7a5b00',
  background: '#FBE08A',
  borderRadius: 4,
  padding: '2px 6px',
};

const vipSyncBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  color: '#15161A',
  background: '#FBE08A',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
};
